(ns ductile.ui.components.sortable
  (:require [clojure.string :as str]
            [nextjournal.clerk.render.hooks :as hooks]))

(defonce !active-drag (atom nil))
(def ^:private drag-state-event "ductile-sortable-drag-state")
(def ^:private drag-cancel-event "ductile-sortable-cancel-drag")

(defn- publish-active-drag! [drag]
  (reset! !active-drag drag)
  (.dispatchEvent js/window (js/CustomEvent. drag-state-event #js {:detail drag})))

(defn cancel-active-drag! []
  (.dispatchEvent js/window (js/Event. drag-cancel-event)))

(defn vec-move [xs from to]
  (let [items (vec xs)
        n (count items)
        from (max 0 (min (dec n) from))
        to (max 0 (min (dec n) to))]
    (if (or (<= n 1) (= from to))
      items
      (let [item (nth items from)
            others (vec (concat (subvec items 0 from)
                                (subvec items (inc from))))]
        (vec (concat (subvec others 0 to)
                     [item]
                     (subvec others to)))))))

(defn- parse-int [v]
  (when-let [s (some-> v str str/trim not-empty)]
    (let [n (js/parseInt s 10)]
      (when-not (js/isNaN n) n))))

(defn- parse-float [v]
  (let [n (js/parseFloat (or v "0"))]
    (if (js/isNaN n) 0 n)))

(defn- pointer-pos [^js event]
  [(.-clientX event) (.-clientY event)])

(defn- axis-allowed? [axis [sx sy] [x y]]
  (let [dx (js/Math.abs (- x sx))
        dy (js/Math.abs (- y sy))]
    (case axis
      :x (and (> dx 6) (>= dx (* dy 1.2)))
      :y (and (> dy 6) (>= dy (* dx 1.2)))
      :both (> (+ dx dy) 6)
      true)))

(defn- effective-axis [axis [sx sy] [x y]]
  (if (= axis :both)
    (if (> (js/Math.abs (- x sx))
           (js/Math.abs (- y sy)))
      :x
      :y)
    axis))

(defn- item-elements [^js container]
  (array-seq (.querySelectorAll container ".ductile-sortable-item:not([data-sortable-transient='true'])")))

(defn- drop-slot [^js container axis start pointer]
  (let [els (item-elements container)
        n (count els)
        axis' (effective-axis axis start pointer)
        [x y] pointer]
    (if (zero? n)
      {:slot 0 :axis axis'}
      (let [first-el (nth els 0)
            last-el (nth els (dec n))
            first-r (.getBoundingClientRect ^js first-el)
            last-r (.getBoundingClientRect ^js last-el)
            p (if (= axis' :x) x y)
            first-edge (if (= axis' :x) (.-left first-r) (.-top first-r))
            last-edge (if (= axis' :x) (.-right last-r) (.-bottom last-r))
            slot (cond
                   (< p (- first-edge 6)) 0
                   (>= p (- last-edge 1)) n
                   :else
                   (loop [i 0]
                     (if (>= i n)
                       n
                       (let [el (nth els i)
                             r (.getBoundingClientRect ^js el)
                             mid (if (= axis' :x)
                                   (+ (.-left r) (/ (.-width r) 2))
                                   (+ (.-top r) (/ (.-height r) 2)))]
                         (if (< p mid)
                           i
                           (recur (inc i)))))))]
        {:slot slot :axis axis'}))))

(defn- create-ghost [^js row-el row-els offset-y]
  (let [r (.getBoundingClientRect row-el)
        source-els (vec (filter some? (or row-els [row-el])))
        ghost (.createElement js/document "div")]
    (set! (.. ghost -style -position) "fixed")
    (set! (.. ghost -style -left) (str (.-left r) "px"))
    (set! (.. ghost -style -top) (str (- (.-top r) (or offset-y 0)) "px"))
    (set! (.. ghost -style -width) (str (.-width r) "px"))
    (set! (.. ghost -style -zIndex) "99999")
    (set! (.. ghost -style -pointerEvents) "none")
    (set! (.. ghost -style -opacity) "0.92")
    (set! (.. ghost -style -boxShadow) "0 6px 16px rgba(0,0,0,0.18)")
    (set! (.. ghost -style -background) "white")
    (set! (.. ghost -style -display) "flex")
    (set! (.. ghost -style -flexDirection) "column")
    (doseq [^js source-el source-els]
      (let [clone (.cloneNode source-el true)]
        (set! (.. clone -style -margin) "0")
        (.appendChild ghost clone)))
    (.appendChild (.-body js/document) ghost)
    ghost))

(defn- row-step [^js container ^js row-el idx]
  (let [els (item-elements container)
        n (count els)
        row-r (.getBoundingClientRect row-el)
        top (.-top row-r)
        h (.-height row-r)]
    (if (> n 1)
      (let [next-step (when (< idx (dec n))
                        (let [r (.getBoundingClientRect ^js (nth els (inc idx)))]
                          (- (.-top r) top)))
            prev-step (when (pos? idx)
                        (let [r (.getBoundingClientRect ^js (nth els (dec idx)))]
                          (- top (.-top r))))]
        (max 1 (or next-step prev-step h)))
      (let [style (.getComputedStyle js/window row-el)
            mt (parse-float (.-marginTop style))
            mb (parse-float (.-marginBottom style))]
        (max 1 (+ h mt mb))))))

(defn- scroll-style? [v]
  (or (= v "auto")
      (= v "scroll")
      (= v "overlay")))

(defn- scrollable? [^js el axis]
  (when el
    (let [style (.getComputedStyle js/window el)
          overflow (.-overflow style)
          overflow-axis (case axis
                          :x (.-overflowX style)
                          :y (.-overflowY style)
                          (.-overflowY style))
          can-scroll? (or (scroll-style? overflow-axis)
                          (scroll-style? overflow))]
      (and can-scroll?
           (case axis
             :x (> (.-scrollWidth el) (.-clientWidth el))
             :y (> (.-scrollHeight el) (.-clientHeight el))
             (> (.-scrollHeight el) (.-clientHeight el)))))))

(defn- nearest-scroll-container [^js el axis]
  (loop [node el]
    (cond
      (nil? node) nil
      (scrollable? node axis) node
      :else (recur (.-parentElement node)))))

(defn- edge-scroll! [^js scroller axis [x y]]
  (when scroller
    (let [r (.getBoundingClientRect scroller)
          threshold 26
          max-step 18
          ratio->step (fn [dist]
                        (let [ratio (/ (max 0 (min threshold dist)) threshold)]
                          (* max-step (- 1 ratio))))
          dy (if (= axis :x)
               0
               (cond
                 (< y (+ (.-top r) threshold)) (- (ratio->step (- y (.-top r))))
                 (> y (- (.-bottom r) threshold)) (ratio->step (- (.-bottom r) y))
                 :else 0))
          dx (if (= axis :y)
               0
               (cond
                 (< x (+ (.-left r) threshold)) (- (ratio->step (- x (.-left r))))
                 (> x (- (.-right r) threshold)) (ratio->step (- (.-right r) x))
                 :else 0))]
      (when (not= 0 dy)
        (set! (.-scrollTop scroller) (+ (.-scrollTop scroller) dy)))
      (when (not= 0 dx)
        (set! (.-scrollLeft scroller) (+ (.-scrollLeft scroller) dx))))))

(defn sortable
  [{:keys [items key-fn render-item on-reorder on-move axis indicator-axis activation-axis disabled? handle-selector
           on-drag-start on-drag-end
           container-class item-class container-attrs item-attrs
           data-testid item-testid-fn
           sortable-id sortable-group
           selected-keys resolve-drag-keys can-start-drag?]
    :or {key-fn identity
         axis :both
         container-attrs {}
         item-attrs {}
         selected-keys #{}}}]
  (let [!container-ref (hooks/use-ref nil)
        activation-axis (or activation-axis axis)
        indicator-axis (or indicator-axis axis)
        !items-ref (hooks/use-ref items)
        !on-reorder-ref (hooks/use-ref on-reorder)
        !on-move-ref (hooks/use-ref on-move)
        !on-drag-start-ref (hooks/use-ref on-drag-start)
        !on-drag-end-ref (hooks/use-ref on-drag-end)
        !drag-ref (hooks/use-ref nil)
        !drag-from (hooks/use-state nil)
        !drag-slot (hooks/use-state nil)
        !drag-axis (hooks/use-state axis)
        !drag-active (hooks/use-state false)
        !row-height (hooks/use-state 0)
        !item-step (hooks/use-state 0)
        !global-drag (hooks/use-state @!active-drag)]
    (set! (.-current !items-ref) items)
    (set! (.-current !on-reorder-ref) on-reorder)
    (set! (.-current !on-move-ref) on-move)
    (set! (.-current !on-drag-start-ref) on-drag-start)
    (set! (.-current !on-drag-end-ref) on-drag-end)

    (hooks/use-effect
     (fn []
       (let [handler (fn [^js e]
                       (reset! !global-drag (.-detail e)))]
         (.addEventListener js/window drag-state-event handler)
         #(do
            (.removeEventListener js/window drag-state-event handler))))
     [])

    (hooks/use-effect
     (fn []
       (let [finish-handler (fn [fire-end? commit?]
                              (when-let [{:keys [from slot active? ghost source-id source-items drag-keys pointer target-id]} (.-current !drag-ref)]
                                (when (and commit? active? (number? slot))
                                  (let [target-el (some-> js/document (.elementFromPoint (first pointer) (second pointer)))
                                        target-container (.closest target-el ".ductile-sortable-container")
                                        target-id (or target-id
                                                      (some-> target-container (.getAttribute "data-sortable-id") keyword)
                                                      source-id)
                                        target-group (some-> target-container (.getAttribute "data-sortable-group") keyword)
                                        in-same-group? (or (nil? sortable-group)
                                                           (= sortable-group target-group))]
                                    (when in-same-group?
                                      (if-let [f (.-current !on-move-ref)]
                                        (f {:source-id source-id
                                            :target-id target-id
                                            :slot slot
                                            :drag-keys (vec drag-keys)
                                            :items source-items})
                                        (when (and (= source-id target-id)
                                                   (number? from))
                                          (let [to (if (> slot from) (dec slot) slot)]
                                            (when (and (number? to) (not= from to))
                                              (when-let [reorder (.-current !on-reorder-ref)]
                                                (reorder {:from from
                                                          :to to
                                                          :items (vec-move source-items from to)})))))))))
                                (when ghost
                                  (.remove ghost))
                                (set! (.-current !drag-ref) nil)
                                (reset! !drag-from nil)
                                (reset! !drag-slot nil)
                                (reset! !drag-active false)
                                (reset! !row-height 0)
                                (reset! !item-step 0)
                                (set! (.. js/document -body -style -userSelect) "")
                                (publish-active-drag! nil)
                                (when (and fire-end? active?)
                                  (when-let [f (.-current !on-drag-end-ref)]
                                    (f)))))
             move-handler (fn [^js event]
                            (when-let [{:keys [from axis start active? pointer-offset anchor-left anchor-top row-el ghost ghost-offset-y drag-row-els]} (.-current !drag-ref)]
                              (let [pointer (pointer-pos event)]
                                (if (not active?)
                                  (when (axis-allowed? activation-axis start pointer)
                                    (when-let [^js container @!container-ref]
                                      (let [axis' (effective-axis axis start pointer)
                                            step (row-step container row-el from)
                                            h (or (some-> row-el (.getBoundingClientRect) .-height) 0)
                                            ghost' (create-ghost row-el drag-row-els ghost-offset-y)]
                                        (set! (.-current !drag-ref)
                                              (assoc (.-current !drag-ref)
                                                     :active? true
                                                     :ghost ghost'
                                                     :item-step step
                                                     :row-height h
                                                     :pointer pointer))
                                        (publish-active-drag! {:source-id sortable-id
                                                               :source-group sortable-group
                                                               :slot from
                                                               :pointer pointer
                                                               :drag-preview-item (:drag-preview-item (.-current !drag-ref))
                                                               :drag-count (max 1 (count (:drag-keys (.-current !drag-ref))))
                                                               :drag-size (* (max 1 (count (:drag-keys (.-current !drag-ref))))
                                                                             (max 1 step))})
                                        (reset! !drag-from from)
                                        (reset! !drag-slot from)
                                        (reset! !drag-axis axis')
                                        (reset! !drag-active true)
                                        (reset! !row-height h)
                                        (reset! !item-step step)
                                        (set! (.. js/document -body -style -userSelect) "none")
                                        (when-let [f (.-current !on-drag-start-ref)]
                                          (f)))))
                                  (do
                                    (.preventDefault event)
                                    (let [[px py] pointer
                                          [off-x off-y] pointer-offset
                                          target-el (js/document.elementFromPoint px py)
                                          target-container (.closest target-el ".ductile-sortable-container")
                                          active-container (or target-container @!container-ref)
                                          source? (= (some-> active-container (.getAttribute "data-sortable-id") keyword)
                                                     sortable-id)
                                          ghost-axis' (effective-axis axis start pointer)
                                          slot-axis (or indicator-axis axis)]
                                      (when ghost
                                        (case axis
                                          :y (do
                                               (set! (.. ghost -style -left) (str anchor-left "px"))
                                               (set! (.. ghost -style -top) (str (- py off-y (or ghost-offset-y 0)) "px")))
                                          :x (do
                                               (set! (.. ghost -style -left) (str (- px off-x) "px"))
                                               (set! (.. ghost -style -top) (str anchor-top "px")))
                                          (do
                                            (set! (.. ghost -style -left) (str (- px off-x) "px"))
                                            (set! (.. ghost -style -top) (str (- py off-y (or ghost-offset-y 0)) "px")))))
                                      (when-let [scroll-container (and active-container
                                                                       (nearest-scroll-container active-container slot-axis))]
                                        (edge-scroll! scroll-container axis pointer))
                                      (let [{:keys [slot axis']}
                                            (if active-container
                                              (let [{:keys [slot axis]} (drop-slot active-container slot-axis start pointer)]
                                                {:slot slot :axis' axis})
                                              {:slot nil :axis' ghost-axis'})
                                            target-id (some-> active-container (.getAttribute "data-sortable-id") keyword)
                                            target-group (some-> active-container (.getAttribute "data-sortable-group") keyword)
                                            in-same-group? (and active-container
                                                                (or (nil? sortable-group)
                                                                    (= sortable-group target-group)))
                                            drag-count (max 1 (count (:drag-keys (.-current !drag-ref))))
                                            drag-size (or (:drag-size @!global-drag)
                                                          (* drag-count (max 1 (or (:item-step (.-current !drag-ref)) @!item-step))))]
                                        (if in-same-group?
                                          (do
                                            (set! (.-current !drag-ref)
                                                  (assoc (.-current !drag-ref)
                                                         :slot slot
                                                         :pointer pointer
                                                         :target-id target-id))
                                            (publish-active-drag! {:source-id sortable-id
                                                                   :source-group sortable-group
                                                                   :target-id target-id
                                                                   :slot slot
                                                                   :pointer pointer
                                                                   :drag-preview-item (:drag-preview-item (.-current !drag-ref))
                                                                   :drag-count drag-count
                                                                   :drag-size drag-size})
                                            (if source?
                                              (do
                                                (reset! !drag-slot slot)
                                                (reset! !drag-axis axis'))
                                              (do
                                                (reset! !drag-slot nil)
                                                (reset! !drag-axis axis'))))
                                          (do
                                            (set! (.-current !drag-ref)
                                                  (assoc (.-current !drag-ref)
                                                         :slot nil
                                                         :pointer pointer
                                                         :target-id nil))
                                            (publish-active-drag! {:source-id sortable-id
                                                                   :source-group sortable-group
                                                                   :target-id nil
                                                                   :slot nil
                                                                   :pointer pointer
                                                                   :drag-preview-item (:drag-preview-item (.-current !drag-ref))
                                                                   :drag-count drag-count
                                                                   :drag-size drag-size})
                                            (reset! !drag-slot nil)
                                            (reset! !drag-axis axis'))))))))))
             up-handler (fn [_] (finish-handler true true))
             cancel-handler (fn [_] (finish-handler true false))
             key-handler (fn [^js event]
                           (when (and (= "Escape" (.-key event))
                                      (.-current !drag-ref))
                             (.preventDefault event)
                             (.stopPropagation event)
                             (finish-handler true false)))]
         (.addEventListener js/window "pointermove" move-handler #js {:passive false})
         (.addEventListener js/window "pointerup" up-handler)
         (.addEventListener js/window "pointercancel" cancel-handler)
         (.addEventListener js/window "keydown" key-handler)
         (.addEventListener js/window drag-cancel-event cancel-handler)
         #(do
            (.removeEventListener js/window "pointermove" move-handler)
            (.removeEventListener js/window "pointerup" up-handler)
            (.removeEventListener js/window "pointercancel" cancel-handler)
            (.removeEventListener js/window "keydown" key-handler)
            (.removeEventListener js/window drag-cancel-event cancel-handler)
            (when-let [{:keys [ghost]} (.-current !drag-ref)]
              (when ghost
                (.remove ghost)))
            (set! (.-current !drag-ref) nil)
            (reset! !drag-from nil)
            (reset! !drag-slot nil)
            (reset! !drag-active false)
            (reset! !row-height 0)
            (reset! !item-step 0)
            (set! (.. js/document -body -style -userSelect) "")
            (publish-active-drag! nil))))
     [])

    (let [on-pointer-down (fn [^js event]
                            (when-not (or disabled? (<= (count items) 0))
                              (let [target (.-target event)
                                    handle-ok? (if (seq handle-selector)
                                                 (boolean (.closest target handle-selector))
                                                 true)
                                    row-el (.closest target ".ductile-sortable-item")
                                    idx (some-> row-el (.getAttribute "data-sortable-index") parse-int)]
                                (when (and handle-ok? (number? idx))
                                  (when-let [^js container @!container-ref]
                                    (let [h (or (some-> row-el (.getBoundingClientRect) .-height) 0)
                                          step (row-step container row-el idx)
                                          item (nth items idx nil)
                                          key (when item (key-fn item))
                                          drag-allowed? (if can-start-drag?
                                                          (boolean (can-start-drag? {:event event
                                                                                     :item item
                                                                                     :idx idx
                                                                                     :key key
                                                                                     :selected-keys selected-keys}))
                                                          true)
                                          drag-keys (if (and key
                                                             (contains? selected-keys key)
                                                             (fn? resolve-drag-keys))
                                                      (vec (or (resolve-drag-keys item idx) [key]))
                                                      (if key [key] []))
                                          key->idx (into {}
                                                         (map-indexed (fn [i it]
                                                                        [(key-fn it) i]))
                                                         items)
                                          drag-indices (vec (keep key->idx drag-keys))
                                          drag-row-els (vec (keep (fn [i]
                                                                    (.querySelector container
                                                                                    (str ".ductile-sortable-item[data-sortable-index='" i "']")))
                                                                  drag-indices))
                                          clicked-index (or (first (keep-indexed (fn [i v] (when (= v idx) i))
                                                                                 drag-indices))
                                                            0)
                                          ghost-offset-y (reduce + 0
                                                                 (map (fn [^js el]
                                                                        (or (some-> el (.getBoundingClientRect) .-height)
                                                                            0))
                                                                      (take clicked-index drag-row-els)))]
                                      (when drag-allowed?
                                        (.preventDefault event)
                                        (set! (.-current !drag-ref)
                                              {:from idx
                                               :slot idx
                                               :axis axis
                                               :source-id sortable-id
                                               :source-group sortable-group
                                               :source-items (vec items)
                                               :drag-keys drag-keys
                                               :drag-preview-item item
                                               :drag-row-els drag-row-els
                                               :ghost-offset-y ghost-offset-y
                                               :start (pointer-pos event)
                                               :pointer (pointer-pos event)
                                               :pointer-offset [(- (.-clientX event) (.-left (.getBoundingClientRect row-el)))
                                                                (- (.-clientY event) (.-top (.getBoundingClientRect row-el)))]
                                               :anchor-left (.-left (.getBoundingClientRect container))
                                               :anchor-top (.-top (.getBoundingClientRect container))
                                               :row-height h
                                               :item-step step
                                               :row-el row-el
                                               :active? false})
                                        (reset! !drag-slot idx)
                                        (reset! !drag-axis axis)
                                        (reset! !row-height h)
                                        (reset! !item-step step)
                                        (when-let [^js el (.-target event)]
                                          (.setPointerCapture el (.-pointerId event))))))))))
          from @!drag-from
          source-slot @!drag-slot
          axis' @!drag-axis
          active? (boolean @!drag-active)
          step (max 1 (or @!item-step 0))
          drag @!global-drag
          source-id (:source-id drag)
          target-id (:target-id drag)
          source-list? (and (some? drag) (= sortable-id source-id))
          hovered-list? (and (some? drag) (= sortable-id target-id))
          source-hovered? (and source-list? hovered-list?)
          target-slot (when hovered-list? (:slot drag))
          target-gap? (and hovered-list?
                           (not source-list?)
                           (number? target-slot))
          target-step (max 1
                           (or (when (and (number? (:drag-size drag))
                                          (number? (:drag-count drag))
                                          (pos? (:drag-count drag)))
                                 (/ (:drag-size drag) (:drag-count drag)))
                               step))
          target-gap-size (max 1
                               (or (:drag-size drag)
                                   (* (max 1 (:drag-count drag 1)) step)
                                   @!row-height
                                   24))
          to (when (and active? (number? from) (number? source-slot))
               (if (> source-slot from) (dec source-slot) source-slot))
          target-gap-offset (when target-gap?
                              (* (max 0 (min (count items) target-slot))
                                 target-step))
          base-rows (vec (map-indexed (fn [idx item]
                                        {:kind :item
                                         :idx idx
                                         :item item})
                                      items))
          rows base-rows
          external-on-pointer-down (:on-pointer-down container-attrs)
          container-attrs (dissoc container-attrs :on-pointer-down)
          combined-pointer-down (fn [^js event]
                                  (when external-on-pointer-down
                                    (external-on-pointer-down event))
                                  (on-pointer-down event))]
      (into
       [:div
        (merge
         {:ref !container-ref
          :class (str "relative ductile-sortable-container " container-class)
          :data-testid data-testid
          :data-sortable-id (some-> sortable-id name)
          :data-sortable-group (some-> sortable-group name)
          :on-pointer-down combined-pointer-down}
         container-attrs)]
       (concat
        (map
         (fn [{:keys [idx item]}]
           (let [item-attrs' (if (fn? item-attrs)
                               (item-attrs item idx)
                               item-attrs)
                 selected-item? (contains? selected-keys (key-fn item))
                 dragging? (and source-list? active? (= idx from))
                 shift-px (cond
                            (and source-list?
                                 active?
                                 (number? from)
                                 (number? to))
                            (cond
                              dragging? (* (- to from) step)
                              (> source-slot from) (if (and (> idx from) (<= idx to)) (- step) 0)
                              (< source-slot from) (if (and (>= idx to) (< idx from)) step 0)
                              :else 0)

                            target-gap?
                            (if (>= idx target-slot) target-gap-size 0)

                            :else 0)
                 transform (when (not= shift-px 0)
                             (if (= indicator-axis :x)
                               (str "translateX(" shift-px "px)")
                               (str "translateY(" shift-px "px)")))
                 row-style (merge
                            {:position "relative"}
                            (when (or (and source-list? active?) target-gap?)
                              {:transition "transform 140ms ease"})
                            (when transform {:transform transform}))]
             [:div
              (merge
               {:key (str (key-fn item))
                :class (str "ductile-sortable-item " item-class
                            (when (and (not disabled?)
                                       selected-item?
                                       (or (nil? handle-selector) (empty? handle-selector)))
                              " cursor-grab"))
                :data-sortable-index idx
                :style row-style}
               (when item-testid-fn
                 {:data-testid (item-testid-fn item idx)})
               item-attrs')
              [:div
               {:style {:opacity (if dragging? 0 1)
                        :transition "opacity 100ms linear"}}
               (render-item item idx)]
              (when (and dragging? source-hovered? (number? source-slot))
                [:div.absolute.inset-0.pointer-events-none.z-20
                 {:style {:background "rgba(37,99,235,0.16)"
                          :border "2px dashed rgba(37,99,235,0.85)"
                          :border-radius "6px"
                          :transition "transform 140ms ease"}}])]))
         rows)
        (when target-gap?
          [[:div
            {:key (str "ductile-target-gap-" (some-> sortable-id name))
             :class "pointer-events-none absolute left-0 right-0 z-20"
             :style (merge
                     {:height (str target-gap-size "px")
                      :transition "transform 140ms ease, height 140ms ease"}
                     (if (= indicator-axis :x)
                       {:top 0
                        :transform (str "translateX(" target-gap-offset "px)")}
                       {:top 0
                        :transform (str "translateY(" target-gap-offset "px)")}))}
            [:div.absolute.inset-0
             {:style {:background "rgba(37,99,235,0.16)"
                      :border "2px dashed rgba(37,99,235,0.85)"
                      :border-radius "6px"}}]]]))))))
